import { useGetPrepareHoldings } from '@hooks/other/useGetPrepareHoldings';
import dayjs from 'dayjs';
import React from 'react';
import { Box, Card, Collapse, Paper, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { Holding } from '@daml.js/dtc-registry-holding/lib/Dtc/Registry/Holding/V3/Holding';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { ExecuteBefore } from '../../../components/ExecuteBefore';
import { useParties } from '../../../contexts/PartiesContext';
import TemplateContract from '../../../utils/TemplateContract';
import { fmt } from '../../../utils/common';
import { isPositiveFloat } from '../../../utils/isPositiveFloat';
import { isValidAmount } from '../../../utils/validateAmount';
import { MaxLimitErrorMessage } from './MaxLimitErrorMessage';
import { useOfferTransferInstruction } from './useOfferTransferInstruction';

interface OfferTransferFormProps {
  holding: TemplateContract<Holding>;
  onClose: () => void;
}
interface FormPayload {
  amount: string;
  reference: string;
  receiver: string;
  executeBefore?: string;
}
export const OfferTransferForm: React.FC<OfferTransferFormProps> = ({ holding, onClose }) => {
  const { party } = useParties();
  const [hasDateError, setDateError] = React.useState(false);
  const defaultExecuteBefore = dayjs().add(3, 'day');
  const [executeBefore, setExecuteBefore] = React.useState(defaultExecuteBefore);
  const onDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) {
      return;
    }
    setDateError(false);
    const isBefore = date.isBefore(dayjs());
    if (isBefore) {
      setDateError(true);
    }
    const now = dayjs();
    const dateWithCurrTime = date
      .set('hour', now.hour())
      .set('minute', now.minute())
      .set('second', now.second());
    setExecuteBefore(dateWithCurrTime);
  };
  const handlePresetDays = (days: number) => {
    setDateError(false);
    const newDate = dayjs().add(days, 'day');
    setExecuteBefore(newDate);
  };

  const prepareHoldings = useGetPrepareHoldings();
  const offerTransferInstruction = useOfferTransferInstruction();
  const [showMaxLimitError, setShowMaxLimitError] = React.useState(false);
  const [openReference, setOpenAddReference] = React.useState(false);

  const onToggle = () => {
    setOpenAddReference(!openReference);
  };
  const [formPayload, setFormPayload] = React.useState<FormPayload>({
    amount: '',
    reference: '',
    receiver: '',
  });

  const amountWithinBalance = React.useMemo(
    () => isValidAmount(formPayload.amount, holding.payload.amount),
    [formPayload.amount, holding.payload.amount],
  );
  const displayedError = amountWithinBalance ? null : 'Transfer amount exceeds current balance.';
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShowMaxLimitError(false);
    switch (name) {
      case 'amount':
        if (isPositiveFloat(value)) {
          setFormPayload((p) => ({ ...p, [name]: value }));
        }
        break;
      default:
        setFormPayload((p) => ({ ...p, [name]: value }));
    }
  };

  const onOfferInstruction = async () => {
    setShowMaxLimitError(false);
    const { holdingCids, exceedsMaxLimit } = prepareHoldings({
      instrumentId: holding.payload.instrument.id,
      registrar: holding.payload.instrument.source,
      amount: formPayload.amount,
      owner: party,
    });

    if (exceedsMaxLimit) {
      setShowMaxLimitError(true);
      return;
    }
    await offerTransferInstruction({
      party,
      receiver: formPayload.receiver,
      amount: formPayload.amount,
      source: holding.payload.instrument.source,
      instrumentId: holding.payload.instrument.id,
      holdingCids,
      reference: formPayload.reference,
      executeBefore: executeBefore.toISOString(),
    });
    setShowMaxLimitError(false);
    onClose();
  };
  const submitFn = onOfferInstruction;
  const disabled =
    !formPayload.amount || !amountWithinBalance || !formPayload.receiver || hasDateError;

  return (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Send from
        </Typography>
        <Box sx={{ p: 1 }} component={Card} variant="outlined">
          <Typography
            variant="body2"
            sx={{
              border: '1px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {party}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Send to
        </Typography>
        <TextField
          name="receiver"
          onChange={onChange}
          autoFocus
          placeholder="Enter party ID"
          size="small"
          fullWidth
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
          Asset
        </Typography>
        <Box
          sx={{ p: 1, overflow: 'hidden', flexDirection: 'column' }}
          component={Card}
          variant="outlined"
        >
          <Typography fontWeight="bold" variant="body2">
            {holding.payload.instrument.id}
          </Typography>
          <Typography
            fontSize={12}
            color="text.secondary"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {holding.payload.instrument.source}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Stack alignItems="center" direction="row">
          <Typography sx={{ mb: 0.5, mr: 1 }} variant="body2" color="secondary" fontWeight="bold">
            Amount
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            <Typography color="text.secondary" sx={{ mr: 1 }} variant="caption">
              Balance:
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {fmt(holding?.payload.amount, 2)}
            </Typography>
            <Typography
              sx={{
                ml: 1,
                maxWidth: '100px',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
              color="text.secondary"
              variant="caption"
            >
              {holding.payload.instrument.id}
            </Typography>
          </Box>
        </Stack>
        <TextField
          name="amount"
          placeholder="0.00"
          autoComplete="off"
          error={!amountWithinBalance}
          helperText={formPayload.amount === '' ? null : displayedError}
          value={formPayload.amount}
          onChange={onChange}
          size="small"
        />
        {showMaxLimitError && <MaxLimitErrorMessage />}
      </Box>
      <Typography sx={{ mt: 2, mb: 0.5 }} variant="body2" color="secondary" fontWeight="bold">
        Execute Before
      </Typography>
      <ExecuteBefore
        handlePresetDays={handlePresetDays}
        selectedDate={executeBefore}
        onChange={onDateChange}
      />
      {hasDateError && (
        <Typography variant="caption" color="error">
          Date must be in the future
        </Typography>
      )}
      <Box onClick={onToggle} sx={{ cursor: 'pointer', mt: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Add reference
        </Typography>
      </Box>
      <Box>
        <Collapse in={openReference}>
          <TextField
            name="reference"
            placeholder="Input reference"
            sx={{ mb: 1 }}
            size="small"
            fullWidth
            value={formPayload.reference}
            onChange={onChange}
          />
        </Collapse>
      </Box>
      <Toolbar />
      <Box elevation={0} component={Paper} sx={{ position: 'sticky', bottom: 0 }}>
        <AsyncButton
          onClick={submitFn}
          enableDefaultPadding
          variant={disabled ? 'contained' : 'contained'}
          size="small"
          fullWidth
          disabled={disabled}
          sx={{ mt: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          Offer
        </AsyncButton>
      </Box>
    </Box>
  );
};
